import { CANDIDATE_FIELDS, CANDIDATE_FIELD_LABELS, CANDIDATE_LAYOUT_FIELDS } from '@crm/contracts';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, FileText, History, Loader2, Pencil, Upload } from 'lucide-react';
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { describeError } from '../../lib/api.ts';
import {
  api,
  type CandidateDetailView,
  type CandidateRecord,
  type FieldDefinitionView,
} from '../../lib/client.ts';
import { arrangeFields, layoutQuery } from '../../lib/custom-fields.ts';
import { uploadCandidateDocument } from '../../lib/documents.ts';
import { useSession } from '../../lib/session.tsx';
import { FieldCell } from '../fields/cell.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs.tsx';
import { ActivityTimeline } from '../records/activity-timeline.tsx';
import { NotesTasksBlock } from '../records/notes-tasks-block.tsx';
import { CandidateJobs } from './candidate-jobs.tsx';
import { CandidatePipelines } from './candidate-pipelines.tsx';
import { useDocumentViewer } from './document-viewer.tsx';
import { HistoryList } from './history-panel.tsx';

export const LAYOUT_QUERY = layoutQuery('candidates');

/**
 * One candidate, drawn the same way on the full page and in the table's side panel
 * (RELAY-944). The header answers "who is this and where are they" at a glance; between the
 * header and the tabs, its Pipelines card: its place in each Candidate Flow pipeline, as a
 * project's page has it. The tabs each load their own query only when opened, so the panel is
 * as cheap as the row it came from until the user asks for more. Notes and Tasks are tabs too,
 * right after Overview — not a column beside the record.
 */
export function CandidateDetail({
  candidate,
  definitions,
  onEdit,
  onChanged,
  fullPageHref,
  initialTab = 'overview',
}: {
  candidate: CandidateDetailView;
  definitions: readonly FieldDefinitionView[];
  onEdit?: (() => void) | undefined;
  /** Something about the candidate changed (a resume upload): refetch what shows it. */
  onChanged?: (() => void | Promise<void>) | undefined;
  /** From the panel: a link to the full page. */
  fullPageHref?: string | undefined;
  initialTab?: 'overview' | 'notes' | 'tasks' | 'activity' | 'documents' | 'jobs' | 'history';
}) {
  const session = useSession();
  const [tab, setTab] = useState<string>(initialTab);
  const [error, setError] = useState<string | null>(null);
  const viewer = useDocumentViewer(setError);
  const [uploading, setUploading] = useState(false);
  const resumeName =
    candidate.resumeDocument?.filename ?? candidate.resumeFilename ?? 'Resume (RecruitCRM)';
  const fileInput = useRef<HTMLInputElement>(null);
  const canUpload = session.can('document.upload');
  // Shortlist places (RELAY-953): only where jobs exist for this viewer.
  const jobsOn = session.isEnabled('jobs.enabled') && session.can('job.read');
  const name = [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'Candidate';
  const canWrite = session.can('candidate.update', { ownerId: candidate.ownerId });
  // The stage is the default pipeline's, shown on its bar; the badge is for a candidate whose
  // stage text is in no pipeline (from before places).
  const placedInDefault = candidate.pipelines.some((p) => p.isDefault && !p.archivedAt);

  const owner = useQuery({
    queryKey: ['users', session.me.organisation.id],
    queryFn: () => api.users(),
    enabled: candidate.ownerId !== null,
    staleTime: 60_000,
  });
  const ownerMember = owner.data?.find((u) => u.id === candidate.ownerId);
  // Loading → an ellipsis; loaded but not a member (left the organisation) → say so, not "…" forever.
  const ownerLabel = owner.isPending
    ? '…'
    : (ownerMember?.name ?? ownerMember?.email ?? 'Not a current member');

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      await uploadCandidateDocument(candidate.id, file, 'resume');
      await onChanged?.();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="candidate-detail">
      <div className="flex min-w-0 flex-col gap-4">
        <header className="bg-card border-border rounded-card shadow-card border p-5 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-semibold tracking-tight break-words">{name}</h2>
            {candidate.stage && !placedInDefault && (
              <Badge data-testid="detail-stage">{candidate.stage}</Badge>
            )}
            {candidate.status && <Badge variant="outline">{candidate.status}</Badge>}
            {candidate.isBlocked && <Badge variant="danger">Blocked</Badge>}
          </div>
          <dl className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {candidate.email && (
              <div>
                <dt className="sr-only">Email</dt>
                <dd>
                  <a className="hover:underline" href={`mailto:${candidate.email}`}>
                    {candidate.email}
                  </a>
                </dd>
              </div>
            )}
            {candidate.phone && (
              <div>
                <dt className="sr-only">Phone</dt>
                <dd>{candidate.phone}</dd>
              </div>
            )}
            {candidate.ownerId && (
              <div>
                <dt className="inline">Owner: </dt>
                <dd className="inline" data-testid="detail-owner">
                  {ownerLabel}
                </dd>
              </div>
            )}
            {candidate.currentRole && (
              <div>
                <dt className="sr-only">Role</dt>
                <dd>{candidate.currentRole}</dd>
              </div>
            )}
          </dl>
          <div className="flex flex-wrap items-center gap-2" data-testid="quick-actions">
            {candidate.resumeDocument || candidate.resumeFileLink ? (
              <Button
                variant="outline"
                size="icon"
                aria-label={`View resume: ${resumeName}`}
                title={`View resume: ${resumeName}`}
                onClick={() =>
                  candidate.resumeDocument
                    ? viewer.open(candidate.resumeDocument)
                    : viewer.openResumeLink(candidate)
                }
                disabled={!session.can('document.read')}
                data-testid="detail-resume"
              >
                <FileText />
              </Button>
            ) : (
              <span className="text-muted-foreground text-sm">No resume</span>
            )}
            {canUpload && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,application/pdf"
                  aria-label="Upload resume file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) void upload(f);
                  }}
                />
                <Button
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  onClick={() => fileInput.current?.click()}
                >
                  {uploading ? <Loader2 className="animate-spin" /> : <Upload />}{' '}
                  {candidate.resumeDocument ? 'Replace resume' : 'Upload resume'}
                </Button>
              </>
            )}
            {onEdit && session.can('candidate.update') && (
              <Button size="sm" onClick={onEdit} data-testid="detail-edit">
                <Pencil /> Edit
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setTab('history')}>
              <History /> History
            </Button>
            {fullPageHref && (
              <a
                className="text-brand ml-auto inline-flex items-center gap-1 text-sm hover:underline"
                href={fullPageHref}
                data-testid="detail-full-page"
              >
                <ExternalLink className="size-3.5" /> Open full page
              </a>
            )}
          </div>
          {error && (
            <p className="text-danger text-sm" role="alert">
              {error}
            </p>
          )}
          {viewer.viewer}
        </header>

        <CandidatePipelines
          candidate={candidate}
          canWrite={canWrite}
          onChanged={async () => {
            await onChanged?.();
          }}
        />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">
              Activity
            </TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">
              Documents
            </TabsTrigger>
            {jobsOn && (
              <TabsTrigger value="jobs" data-testid="tab-jobs">
                Jobs
              </TabsTrigger>
            )}
            <TabsTrigger value="history" data-testid="tab-history">
              History
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <OverviewTab candidate={candidate} definitions={definitions} canWrite={canWrite} />
          </TabsContent>
          <TabsContent value="activity">
            {tab === 'activity' && <ActivityTimeline entityId={candidate.id} />}
          </TabsContent>
          <TabsContent value="documents">
            {tab === 'documents' && <DocumentsTab candidateId={candidate.id} />}
          </TabsContent>
          {jobsOn && (
            <TabsContent value="jobs">
              {tab === 'jobs' && <CandidateJobs candidateId={candidate.id} name={name} />}
            </TabsContent>
          )}
          <TabsContent value="history">
            {tab === 'history' && <HistoryList candidateId={candidate.id} />}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function OverviewTab({
  candidate,
  definitions,
  canWrite,
}: {
  candidate: CandidateRecord;
  definitions: readonly FieldDefinitionView[];
  canWrite: boolean;
}) {
  const layout = useQuery({
    queryKey: LAYOUT_QUERY,
    queryFn: () => api.layouts.get('candidates'),
    staleTime: 60_000,
  });
  const groups = useMemo(
    () =>
      layout.data ? arrangeFields(layout.data.layout, definitions, CANDIDATE_LAYOUT_FIELDS) : [],
    [layout.data, definitions],
  );
  // Notes and tasks first (Overview of a project does the same), then the field groups.
  const notesTasks = (
    <NotesTasksBlock entityType="candidates" entityId={candidate.id} canWrite={canWrite} />
  );
  if (layout.isPending)
    return (
      <div className="flex flex-col gap-4">
        {notesTasks}
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      </div>
    );
  if (layout.error != null)
    return (
      <div className="flex flex-col gap-4">
        {notesTasks}
        <p className="text-danger text-sm" role="alert">
          {describeError(layout.error)}
        </p>
      </div>
    );
  return (
    <div className="flex flex-col gap-4">
      {notesTasks}
      <div className="flex flex-col gap-4" data-testid="overview-groups">
        {groups.map((g) => (
          <section
            key={g.name}
            className="border-border bg-card rounded-xl border p-3"
            data-testid="overview-group"
          >
            <h3 className="mb-2 text-sm font-medium">{g.name}</h3>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {g.fields.map((f) => (
                <Field key={f} field={f} candidate={candidate} definitions={definitions} />
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  );
}

function Field({
  field,
  candidate,
  definitions,
}: {
  field: string;
  candidate: CandidateRecord;
  definitions: readonly FieldDefinitionView[];
}) {
  let label: string;
  let node: ReactNode;
  if (field.startsWith('custom.')) {
    const def = definitions.find((d) => `custom.${d.key}` === field);
    if (!def) return null;
    label = def.label;
    node = <FieldCell type={def.type} value={candidate.custom?.[def.key]} format={def.format} />;
  } else {
    label = CANDIDATE_FIELD_LABELS[field] ?? field;
    const type = CANDIDATE_FIELDS[field as keyof typeof CANDIDATE_FIELDS] ?? 'text';
    node = <FieldCell type={type} value={(candidate as Record<string, unknown>)[field]} />;
  }
  return (
    <div className="flex flex-col" data-testid={`field-${field}`}>
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="truncate">{node}</dd>
    </div>
  );
}

function DocumentsTab({ candidateId }: { candidateId: string }) {
  const docs = useQuery({
    queryKey: ['candidate-documents', candidateId],
    queryFn: () => api.candidates.documents(candidateId),
  });
  const [error, setError] = useState<string | null>(null);
  const viewer = useDocumentViewer(setError);
  if (docs.isPending)
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    );
  if (docs.error != null)
    return (
      <p className="text-danger text-sm" role="alert">
        {describeError(docs.error)}
      </p>
    );
  return (
    <div className="flex flex-col gap-2" data-testid="documents-list">
      {docs.data.length === 0 && <p className="text-muted-foreground text-sm">No documents yet.</p>}
      {docs.data.map((d) => (
        <div
          key={d.id}
          className="border-border bg-card flex items-center gap-3 rounded-xl border p-2.5 text-sm"
          data-testid="document-row"
        >
          <FileText className="text-muted-foreground size-4 shrink-0" />
          <button
            type="button"
            className="text-brand truncate hover:underline"
            onClick={() => viewer.open(d)}
          >
            {d.filename}
          </button>
          <Badge variant="outline">{d.kind}</Badge>
          {d.isCurrent && <Badge>current</Badge>}
          <TextStatus status={d.textStatus} />
          <span className="text-muted-foreground ml-auto text-xs">
            {(d.sizeBytes / 1024).toFixed(0)} KB ·{' '}
            {d.completedAt ? new Date(d.completedAt).toLocaleDateString() : ''}
          </span>
        </div>
      ))}
      {error && (
        <p className="text-danger text-sm" role="alert">
          {error}
        </p>
      )}
      {viewer.viewer}
    </div>
  );
}

/**
 * Whether a document's text is in search yet (RELAY-943 part 2). Only documents that were
 * queued show anything: `none` means "not a resume, never extracted", which needs no badge.
 */
const TEXT_STATUS: Record<
  string,
  { label: string; variant: 'secondary' | 'success' | 'warning' | 'danger' }
> = {
  pending: { label: 'Indexing…', variant: 'secondary' },
  processing: { label: 'Indexing…', variant: 'secondary' },
  done: { label: 'Searchable', variant: 'success' },
  unsupported: { label: 'Not searchable', variant: 'warning' },
  failed: { label: 'Text extraction failed', variant: 'danger' },
};
function TextStatus({ status }: { status: string }) {
  const s = TEXT_STATUS[status];
  if (!s) return null;
  return (
    <Badge variant={s.variant} data-testid="document-text-status">
      {s.label}
    </Badge>
  );
}
